import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Feature, FeatureCollection, Geometry, Point } from "geojson";
import maplibregl, { GeoJSONSource, type MapGeoJSONFeature } from "maplibre-gl";
import { feature as topojsonFeature } from "topojson-client";
import type { RegionRow } from "../../types/tabs";
import { countryNameToIso, getCountryCenter } from "./countryIsoMap";
import countriesTopology from "world-atlas/countries-110m.json";
import {
  applyEnglishPrimaryMapLabels,
  FOLLOWERS_MAP_VECTOR_STYLE_URL,
  getChoroplethBeforeLayerId,
} from "../../../organicMedia/components/maps/followersMapBaseStyle";
import {
  FOLLOWERS_MAP_PIN_CLICK_ZOOM,
  FOLLOWERS_MAP_PIN_HOVER_IMAGE_ID,
  FOLLOWERS_MAP_PIN_IMAGE_ID,
  ensureStandardMapPinOnMap,
  mapPinWeightedIconSize,
  MAP_PIN_BASE_ICON_OPACITY_SOLID,
  MAP_PIN_FOCUS_ICON_OPACITY,
} from "../../../organicMedia/components/maps/followersMapPin";
import "./WorldMap.scss";

const WA_COUNTRIES_SOURCE = "wa-demographics-countries";
const WA_COUNTRY_FILL = "wa-demographics-country-fill";
const WA_COUNTRY_LINE = "wa-demographics-country-line";
const WA_PIN_SOURCE = "wa-demographics-pins";
const WA_PIN_BASE_LAYER = "wa-demographics-pins-layer-base";
const WA_PIN_FOCUS_LAYER = "wa-demographics-pins-layer-focus";
const WA_HIGHLIGHT_PIN_SOURCE = "wa-demographics-highlight-pin";
const WA_HIGHLIGHT_PIN_LAYER = "wa-demographics-highlight-pin-layer";
const WA_MAIN_PIN_LAYERS = [WA_PIN_FOCUS_LAYER, WA_PIN_BASE_LAYER];

const DEFAULT_FILL = "#eef2f7";
const DEFAULT_CENTER: [number, number] = [20, 24];
const DEFAULT_ZOOM = 1.25;

const LIST_HOVER_SMALL = new Set([
  "VAT",
  "SMR",
  "MCO",
  "LIE",
  "AND",
  "GIB",
  "LUX",
  "MLT",
  "SGP",
  "BHR",
  "FSM",
  "MHL",
  "TUV",
  "NRU",
  "KIR",
  "WSM",
  "TON",
  "PYF",
  "GUM",
  "ASM",
  "MNP",
  "CYM",
  "BMU",
  "PSE",
  "BLM",
  "MAF",
  "SXM",
  "CUW",
  "ABW",
  "BRB",
  "ATG",
  "DMA",
  "GRD",
  "KNA",
  "LCA",
  "VCT",
  "VIR",
  "COK",
  "NIU",
  "TKL",
  "WLF",
]);

function zoomForListHover(iso: string): number {
  if (LIST_HOVER_SMALL.has(iso)) {
    return 6;
  }
  if (iso === "CHN") {
    return 3.2;
  }
  if (iso === "RUS" || iso === "USA" || iso === "CAN" || iso === "AUS" || iso === "BRA" || iso === "IND") {
    return 3.8;
  }
  return 4.5;
}

function valueToFillColor(value: number, maxV: number): string {
  const t = Math.min(1, Math.max(0, value / maxV));
  const h = 214;
  const s = Math.round(32 + t * 58);
  const l = Math.round(92 - t * 47);
  return `hsl(${h}, ${s}%, ${l}%)`;
}

function buildFillColorExpression(regions: RegionRow[], maxV: number) {
  const pairs: (string | number | boolean)[] = [];
  for (const r of regions) {
    const iso = countryNameToIso(r.name);
    if (iso) {
      pairs.push(iso, valueToFillColor(r.value, maxV));
    }
  }
  return ["match", ["get", "iso"], ...pairs, DEFAULT_FILL];
}

function extendBoundsWithCoords(bounds: maplibregl.LngLatBounds, coords: unknown): void {
  if (!Array.isArray(coords)) {
    return;
  }
  if (coords.length >= 2 && typeof coords[0] === "number") {
    const lng = coords[0] as number;
    const lat = coords[1] as number;
    bounds.extend([lng, lat]);
    return;
  }
  for (const c of coords) {
    extendBoundsWithCoords(bounds, c);
  }
}

function boundsFromGeometry(geometry: Geometry): maplibregl.LngLatBounds | null {
  const bounds = new maplibregl.LngLatBounds();
  extendBoundsWithCoords(bounds, (geometry as { coordinates?: unknown }).coordinates);
  if (!bounds.isEmpty()) {
    return bounds;
  }
  return null;
}

type WorldProps = { name?: string; iso?: string | null; gid?: string };

const TOPO_COUNTRIES = topojsonFeature(
  countriesTopology as never,
  (countriesTopology as unknown as { objects: { countries: unknown } }).objects.countries as never
) as unknown as FeatureCollection<Geometry, { name?: string }>;

function enrichWorldCountries(): FeatureCollection<Geometry, WorldProps> {
  const features = TOPO_COUNTRIES.features.map((f, i) => {
    const name = String(f.properties?.name ?? "");
    const iso = countryNameToIso(name) ?? null;
    const gid = iso ?? `x:${i}:${name}`;
    return {
      ...f,
      properties: {
        ...f.properties,
        name,
        iso,
        gid,
      },
    };
  });
  return { type: "FeatureCollection", features };
}

const ENRICHED_WORLD = enrichWorldCountries();

interface WorldMapProps {
  regions: RegionRow[];
  metricLabel: string;
  hoveredRegion?: RegionRow | null;
}

const WorldMap: FC<WorldMapProps> = ({ regions, metricLabel, hoveredRegion }) => {
  const { t, i18n } = useTranslation();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const hoveredCountryGidRef = useRef<string | null>(null);
  const hoveredPinIsoRef = useRef<string | number | null>(null);
  const activeZoomIsoRef = useRef<string | null>(null);
  const maxValueRef = useRef(1);

  const [tooltip, setTooltip] = useState<{
    countryName: string;
    value: number | undefined;
    x: number;
    y: number;
  } | null>(null);

  const isoToRegion = useMemo(() => {
    const m = new Map<string, RegionRow>();
    for (const r of regions) {
      const iso = countryNameToIso(r.name);
      if (iso) {
        m.set(iso, r);
      }
    }
    return m;
  }, [regions]);

  const hoveredIso = hoveredRegion ? countryNameToIso(hoveredRegion.name) ?? null : null;

  const maxRegionValue = useMemo(() => Math.max(1, ...regions.map(r => r.value)), [regions]);
  maxValueRef.current = maxRegionValue;

  const pinCollection = useMemo(() => {
    const features: Feature<Point, { iso: string; name: string; value: number }>[] = [];
    for (const r of regions) {
      const iso = countryNameToIso(r.name);
      if (!iso) {
        continue;
      }
      const c = getCountryCenter(iso);
      if (!c) {
        continue;
      }
      features.push({
        type: "Feature",
        geometry: { type: "Point", coordinates: c },
        properties: { iso, name: r.name, value: r.value },
      });
    }
    return { type: "FeatureCollection", features } as FeatureCollection<
      Point,
      { iso: string; name: string; value: number }
    >;
  }, [regions]);

  const regionsRef = useRef(regions);
  regionsRef.current = regions;
  const isoToRegionRef = useRef(isoToRegion);
  isoToRegionRef.current = isoToRegion;
  const pinCollectionRef = useRef(pinCollection);
  pinCollectionRef.current = pinCollection;
  const tRef = useRef(t);
  tRef.current = t;

  const clearFillHover = useCallback((map: maplibregl.Map) => {
    const id = hoveredCountryGidRef.current;
    if (id !== undefined && id !== null && map.getSource(WA_COUNTRIES_SOURCE)) {
      map.setFeatureState({ source: WA_COUNTRIES_SOURCE, id }, { hover: false });
    }
    hoveredCountryGidRef.current = null;
  }, []);

  const clearPinHover = useCallback((map: maplibregl.Map) => {
    const prev = hoveredPinIsoRef.current;
    if (prev !== null && prev !== undefined && map.getSource(WA_PIN_SOURCE)) {
      map.removeFeatureState({ source: WA_PIN_SOURCE, id: prev }, "hover");
    }
    hoveredPinIsoRef.current = null;
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map?.loaded()) {
      return;
    }
    if (!hoveredIso) {
      return;
    }
    const c = getCountryCenter(hoveredIso);
    if (!c) {
      return;
    }
    map.flyTo({
      center: c,
      zoom: zoomForListHover(hoveredIso),
      essential: true,
    });
  }, [hoveredIso, hoveredRegion?.name]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) {
      return;
    }

    let destroyed = false;

    const map = new maplibregl.Map({
      container: el,
      style: FOLLOWERS_MAP_VECTOR_STYLE_URL,
      center: DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
      attributionControl: false,
      preserveDrawingBuffer: true,
    });

    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-right");
    mapRef.current = map;

    map.on("load", () => {
      applyEnglishPrimaryMapLabels(map);
      const beforeLabels = getChoroplethBeforeLayerId(map);

      if (!map.getSource(WA_COUNTRIES_SOURCE)) {
        map.addSource(WA_COUNTRIES_SOURCE, {
          type: "geojson",
          data: ENRICHED_WORLD,
          promoteId: "gid",
        });
      }

      const maxV = maxValueRef.current;
      const regs = regionsRef.current;

      if (!map.getLayer(WA_COUNTRY_FILL)) {
        map.addLayer(
          {
            id: WA_COUNTRY_FILL,
            type: "fill",
            source: WA_COUNTRIES_SOURCE,
            paint: {
              "fill-color": buildFillColorExpression(regs, maxV),
              "fill-opacity": 0.85,
            },
          } as never,
          beforeLabels
        );
      }

      if (!map.getLayer(WA_COUNTRY_LINE)) {
        map.addLayer(
          {
            id: WA_COUNTRY_LINE,
            type: "line",
            source: WA_COUNTRIES_SOURCE,
            paint: {
              "line-color": [
                "case",
                ["boolean", ["feature-state", "hover"], false],
                "#3079bb",
                "#cfd8e6",
              ],
              "line-width": [
                "case",
                ["boolean", ["feature-state", "hover"], false],
                2,
                0.55,
              ],
            },
          } as never,
          beforeLabels
        );
      }

      const queryPinLayers = (): string[] =>
        [WA_HIGHLIGHT_PIN_LAYER, ...WA_MAIN_PIN_LAYERS].filter(id => Boolean(map.getLayer(id)));

      const onMove = (e: maplibregl.MapMouseEvent) => {
        const pinLayers = queryPinLayers();
        const layers = [...pinLayers, WA_COUNTRY_FILL].filter(id => Boolean(map.getLayer(id)));
        const hits = map.queryRenderedFeatures(e.point, { layers });
        const top = hits[0] as MapGeoJSONFeature | undefined;
        const topId = top?.layer?.id;

        if (
          topId === WA_HIGHLIGHT_PIN_LAYER ||
          topId === WA_PIN_BASE_LAYER ||
          topId === WA_PIN_FOCUS_LAYER
        ) {
          clearFillHover(map);
          activeZoomIsoRef.current = null;

          if (topId === WA_HIGHLIGHT_PIN_LAYER) {
            clearPinHover(map);
          } else if (topId === WA_PIN_BASE_LAYER || topId === WA_PIN_FOCUS_LAYER) {
            const fid = top?.id;
            if (fid !== undefined && fid !== null) {
              if (hoveredPinIsoRef.current !== fid) {
                clearPinHover(map);
                hoveredPinIsoRef.current = fid;
                map.setFeatureState({ source: WA_PIN_SOURCE, id: fid }, { hover: true });
              }
            }
          }

          const name = String(top?.properties?.name ?? "");
          const value = Number(top?.properties?.value ?? 0);
          map.getCanvas().style.cursor = "pointer";
          setTooltip({
            countryName: name,
            value: Number.isFinite(value) ? value : undefined,
            x: e.originalEvent.clientX,
            y: e.originalEvent.clientY,
          });
          return;
        }

        clearPinHover(map);

        const fillHit = hits.find(h => h.layer?.id === WA_COUNTRY_FILL) as MapGeoJSONFeature | undefined;

        if (fillHit?.properties) {
          const iso = fillHit.properties.iso as string | undefined;
          const gid = fillHit.properties.gid as string | undefined;
          const region = iso ? isoToRegionRef.current.get(iso) : undefined;
          const fallbackName = String(
            fillHit.properties.name ?? iso ?? tRef.current("webAnalytics.map.unknown")
          );

          if (gid && gid !== hoveredCountryGidRef.current) {
            clearFillHover(map);
            hoveredCountryGidRef.current = gid;
            map.setFeatureState({ source: WA_COUNTRIES_SOURCE, id: gid }, { hover: true });
          }

          map.getCanvas().style.cursor = iso ? "pointer" : "";
          setTooltip({
            countryName: region?.name ?? fallbackName,
            value: region?.value,
            x: e.originalEvent.clientX,
            y: e.originalEvent.clientY,
          });

          if (iso && fillHit.geometry) {
            if (activeZoomIsoRef.current !== iso) {
              const b = boundsFromGeometry(fillHit.geometry as Geometry);
              if (b && !b.isEmpty()) {
                activeZoomIsoRef.current = iso;
                map.fitBounds(b, { padding: 28, maxZoom: 5.5, duration: 450 });
              }
            }
          }
          return;
        }

        clearFillHover(map);
        activeZoomIsoRef.current = null;
        map.getCanvas().style.cursor = "";
        setTooltip(null);
      };

      const onLeave = () => {
        clearFillHover(map);
        clearPinHover(map);
        activeZoomIsoRef.current = null;
        setTooltip(null);
        map.getCanvas().style.cursor = "";
      };

      const onClick = (e: maplibregl.MapMouseEvent) => {
        const pinLayers = queryPinLayers();
        const pinHits = map.queryRenderedFeatures(e.point, { layers: pinLayers });
        const p = pinHits[0] as MapGeoJSONFeature | undefined;
        if (p?.geometry?.type === "Point") {
          const [lng, lat] = (p.geometry as Point).coordinates;
          map.easeTo({ center: [lng, lat], zoom: FOLLOWERS_MAP_PIN_CLICK_ZOOM, duration: 500 });
          return;
        }

        const fillHits = map.queryRenderedFeatures(e.point, { layers: [WA_COUNTRY_FILL] });
        const f = fillHits[0] as MapGeoJSONFeature | undefined;
        if (f?.geometry) {
          const b = boundsFromGeometry(f.geometry as Geometry);
          if (b && !b.isEmpty()) {
            map.fitBounds(b, { padding: 36, maxZoom: 6, duration: 500 });
          }
        }
      };

      map.on("mousemove", onMove);
      map.on("mouseout", onLeave);
      map.on("click", onClick);

      void (async () => {
        const pinOk = await ensureStandardMapPinOnMap(map);
        if (!pinOk || destroyed || mapRef.current !== map) {
          return;
        }

        if (!map.getSource(WA_PIN_SOURCE)) {
          map.addSource(WA_PIN_SOURCE, {
            type: "geojson",
            data: pinCollectionRef.current,
            promoteId: "iso",
          });
        }

        if (!map.getLayer(WA_PIN_BASE_LAYER)) {
          map.addLayer({
            id: WA_PIN_BASE_LAYER,
            type: "symbol",
            source: WA_PIN_SOURCE,
            layout: {
              "icon-image": FOLLOWERS_MAP_PIN_IMAGE_ID,
              "icon-size": mapPinWeightedIconSize(maxValueRef.current, "value"),
              "icon-allow-overlap": true,
              "icon-ignore-placement": true,
              "icon-anchor": "bottom",
            },
            paint: { "icon-opacity": MAP_PIN_BASE_ICON_OPACITY_SOLID },
          } as never);
        }

        if (!map.getLayer(WA_PIN_FOCUS_LAYER)) {
          map.addLayer({
            id: WA_PIN_FOCUS_LAYER,
            type: "symbol",
            source: WA_PIN_SOURCE,
            layout: {
              "icon-image": FOLLOWERS_MAP_PIN_HOVER_IMAGE_ID,
              "icon-size": mapPinWeightedIconSize(maxValueRef.current, "value"),
              "icon-allow-overlap": true,
              "icon-ignore-placement": true,
              "icon-anchor": "bottom",
            },
            paint: { "icon-opacity": MAP_PIN_FOCUS_ICON_OPACITY },
          } as never);
        }

        if (!map.getSource(WA_HIGHLIGHT_PIN_SOURCE)) {
          map.addSource(WA_HIGHLIGHT_PIN_SOURCE, {
            type: "geojson",
            data: { type: "FeatureCollection", features: [] },
            promoteId: "iso",
          });
        }

        if (!map.getLayer(WA_HIGHLIGHT_PIN_LAYER)) {
          map.addLayer({
            id: WA_HIGHLIGHT_PIN_LAYER,
            type: "symbol",
            source: WA_HIGHLIGHT_PIN_SOURCE,
            layout: {
              "icon-image": FOLLOWERS_MAP_PIN_HOVER_IMAGE_ID,
              "icon-size": mapPinWeightedIconSize(maxValueRef.current, "value"),
              "icon-allow-overlap": true,
              "icon-ignore-placement": true,
              "icon-anchor": "bottom",
            },
            paint: { "icon-opacity": 1 },
          } as never);
        }

        try {
          map.moveLayer(WA_PIN_BASE_LAYER);
          map.moveLayer(WA_PIN_FOCUS_LAYER);
          map.moveLayer(WA_HIGHLIGHT_PIN_LAYER);
        } catch {
          /* style may omit expected ordering */
        }
      })();
    });

    return () => {
      destroyed = true;
      mapRef.current = null;
      map.remove();
    };
    // Map is created once; handlers read latest regions via refs and the sync effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- stable map instance
  }, [clearFillHover, clearPinHover]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map?.loaded() || !map.getSource(WA_COUNTRIES_SOURCE)) {
      return;
    }
    const maxV = maxRegionValue;
    map.setPaintProperty(WA_COUNTRY_FILL, "fill-color", buildFillColorExpression(regions, maxV));
    const pinSrc = map.getSource(WA_PIN_SOURCE) as GeoJSONSource | undefined;
    if (pinSrc) {
      pinSrc.setData(pinCollection);
    }
    for (const lid of WA_MAIN_PIN_LAYERS) {
      if (map.getLayer(lid)) {
        map.setLayoutProperty(lid, "icon-size", mapPinWeightedIconSize(maxV, "value"));
      }
    }
    if (map.getLayer(WA_HIGHLIGHT_PIN_LAYER)) {
      map.setLayoutProperty(WA_HIGHLIGHT_PIN_LAYER, "icon-size", mapPinWeightedIconSize(maxV, "value"));
    }

    const hiSrc = map.getSource(WA_HIGHLIGHT_PIN_SOURCE) as GeoJSONSource | undefined;
    if (hiSrc) {
      if (!hoveredIso) {
        hiSrc.setData({ type: "FeatureCollection", features: [] });
      } else {
        const c = getCountryCenter(hoveredIso);
        const row = hoveredRegion ?? isoToRegion.get(hoveredIso);
        if (c && row) {
          hiSrc.setData({
            type: "FeatureCollection",
            features: [
              {
                type: "Feature",
                properties: {
                  iso: hoveredIso,
                  name: row.name,
                  value: row.value,
                },
                geometry: { type: "Point", coordinates: c },
              },
            ],
          });
        } else {
          hiSrc.setData({ type: "FeatureCollection", features: [] });
        }
      }
    }
  }, [regions, maxRegionValue, pinCollection, hoveredIso, hoveredRegion, isoToRegion]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) {
      return;
    }
    const onLang = () => {
      requestAnimationFrame(() => map.resize());
    };
    i18n.on("languageChanged", onLang);
    return () => {
      i18n.off("languageChanged", onLang);
    };
  }, [i18n]);

  const showBarHoverTooltip = Boolean(hoveredRegion && !tooltip);

  return (
    <section className="world-map" aria-label={t("webAnalytics.map.regionAria")}>
      {showBarHoverTooltip && (
        <div className="world-map__bar-hover-tooltip">
          <span className="world-map__tooltip-country">{hoveredRegion!.name}</span>
          <span className="world-map__tooltip-value">
            {hoveredRegion!.value.toLocaleString()} {metricLabel}
          </span>
        </div>
      )}
      <div ref={containerRef} className="world-map__canvas" />
      {tooltip && (
        <div
          className="world-map__tooltip"
          style={{
            left: tooltip.x,
            top: tooltip.y,
            transform: "translate(12px, 12px)",
          }}
        >
          <span className="world-map__tooltip-country">{tooltip.countryName}</span>
          <span className="world-map__tooltip-value">
            {tooltip.value === undefined ? "-" : tooltip.value.toLocaleString()} {metricLabel}
          </span>
        </div>
      )}
    </section>
  );
};

export default WorldMap;
